import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const AMENITIES = [
  ["additional_bedroom", "amenities_bedroom"],
  ["garage_parking", "amenities_parking"],
  ["pool", "amenities_pool"],
  ["jacuzzi", "amenities_jacuzzi"],
  ["gym", "amenities_gym"],
  ["chef", "amenities_chef"],
  ["breakfast", "amenities_breakfast"],
];

export default function ReviewPage() {
  const { token } = useParams();
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [rating, setRating] = useState(5);
  const [f, setF] = useState({
    display_name: "",
    quote: "",
    private_note: "",
    publish_consent: false,
    amenities_other: "",
  });
  const [amen, setAmen] = useState({});
  const [welcomeBack, setWelcomeBack] = useState(null);

  useEffect(() => {
    api.get(`/reviews/token/${token}`)
      .then(r => {
        setInfo(r.data);
        setF(prev => ({ ...prev, display_name: r.data.guest_name || "" }));
      })
      .catch(e => setError(e.response?.data?.detail || t("review.invalid")));
  }, [token, t]);

  async function submit(e) {
    e.preventDefault();
    setState("submitting");
    try {
      const { data } = await api.post(`/reviews/token/${token}`, {
        rating,
        display_name: f.display_name,
        quote: f.quote,
        private_note: f.private_note,
        publish_consent: !!f.publish_consent,
        amenities: amen,
        amenities_other: f.amenities_other,
      });
      if (data && data.welcome_back) setWelcomeBack(data.welcome_back);
      setState("done");
    } catch (e2) {
      setState("error");
      setError(e2.response?.data?.detail || t("review.error"));
    }
  }

  if (error) {
    return (
      <>
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-32 min-h-[60vh] text-center">
          <div className="overline mb-3">Ithaca Garden Villa</div>
          <h1 className="font-serif text-3xl md:text-4xl font-light">{error}</h1>
        </main>
        <Footer />
      </>
    );
  }

  if (!info) {
    return (
      <>
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-32 min-h-[60vh] text-center text-ionian/50">Loading…</main>
        <Footer />
      </>
    );
  }

  if (state === "done" || info.already_submitted) {
    return (
      <>
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-24 md:py-32 min-h-[60vh]" data-testid="review-success">
          <div className="overline mb-3">{t("review.overline")}</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight">
            {info.already_submitted && state !== "done" ? t("review.already") : t("review.success")}
          </h1>

          {welcomeBack && (
            <div className="mt-10 border border-brass/40 bg-cream p-6 md:p-8" data-testid="welcome-back-code">
              <div className="overline text-brass">A private gift · come back to Kioni</div>
              <div className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
                €{Math.round(welcomeBack.discount_eur)} off your next stay.
              </div>
              <div className="mt-5 font-mono text-2xl tracking-widest text-ionian">
                {welcomeBack.code}
              </div>
              <div className="text-xs text-ink/60 italic mt-4 leading-relaxed">
                One-time use · tied to your email · valid for the next 12 months. We've also sent this to your inbox.
                Simply enter it at checkout when you next book.
              </div>
            </div>
          )}

          <p className="mt-10 text-ink/70">Warmly, the owner · {info.villa_name}</p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Nav />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 min-h-[70vh]">
        <div className="overline mb-3">{t("review.overline")}</div>
        <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight" data-testid="review-heading">
          {t("review.heading")}
        </h1>
        <p className="text-ink/70 mt-6 max-w-xl leading-relaxed">{t("review.body")}</p>
        <div className="text-xs font-mono text-ionian/50 mt-4">
          {info.guest_name} · {info.checkin} → {info.checkout}
        </div>

        <form onSubmit={submit} className="mt-12 space-y-10" data-testid="review-form">
          {/* Rating */}
          <div>
            <div className="overline mb-3">{t("review.rating_label")}</div>
            <div className="flex gap-2" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  data-testid={`review-star-${n}`}
                  aria-label={`${n} star`}
                  className={`w-10 h-10 flex items-center justify-center text-2xl transition ${n <= rating ? "text-brass" : "text-ionian/25 hover:text-brass/60"}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          {/* Public quote */}
          <div>
            <div className="overline mb-2">{t("review.quote_label")}</div>
            <textarea
              rows={3}
              data-testid="review-quote"
              value={f.quote}
              onChange={e => setF({ ...f, quote: e.target.value })}
              placeholder={t("review.quote_placeholder")}
              className="editorial-input w-full font-serif italic text-lg"
            />
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <div className="overline mb-1">{t("review.display_label")}</div>
                <input
                  data-testid="review-display-name"
                  value={f.display_name}
                  onChange={e => setF({ ...f, display_name: e.target.value })}
                  placeholder={t("review.display_placeholder")}
                  className="editorial-input w-full"
                />
              </div>
              <label className="flex items-center gap-3 text-sm text-ink/75">
                <input
                  type="checkbox"
                  data-testid="review-consent"
                  checked={!!f.publish_consent}
                  onChange={e => setF({ ...f, publish_consent: e.target.checked })}
                />
                <span>{t("review.quote_consent")}</span>
              </label>
            </div>
          </div>

          {/* Private note */}
          <div>
            <div className="overline mb-2">{t("review.private_label")}</div>
            <textarea
              rows={3}
              data-testid="review-private"
              value={f.private_note}
              onChange={e => setF({ ...f, private_note: e.target.value })}
              placeholder={t("review.private_placeholder")}
              className="editorial-input w-full"
            />
          </div>

          {/* Amenities */}
          <div>
            <div className="overline">{t("review.amenities_label")}</div>
            <div className="text-sm text-ink/60 mt-2 mb-4">{t("review.amenities_body")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="review-amenities">
              {AMENITIES.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`review-amenity-${key}`}
                    checked={!!amen[key]}
                    onChange={e => setAmen(a => ({ ...a, [key]: e.target.checked }))}
                  />
                  {t(`review.${label}`)}
                </label>
              ))}
            </div>
            <div className="mt-6">
              <div className="overline mb-2">{t("review.amenities_other_label")}</div>
              <input
                data-testid="review-amenities-other"
                value={f.amenities_other}
                onChange={e => setF({ ...f, amenities_other: e.target.value })}
                placeholder={t("review.amenities_other_placeholder")}
                className="editorial-input w-full"
              />
            </div>
          </div>

          {error && <div data-testid="review-error" className="text-terracotta text-sm">{error}</div>}

          <div>
            <button
              type="submit"
              disabled={state === "submitting"}
              data-testid="review-submit"
              className="px-8 py-3 bg-ionian text-stone_white text-xs uppercase tracking-widest hover:bg-ionian/90 transition disabled:opacity-60"
            >
              {state === "submitting" ? "…" : t("review.submit")}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </>
  );
}
